import moment from 'moment';
import _ from 'lodash';
import InvigilatorMenuCandidateCanPauseModalController from './modalCtrl/InvigilatorMenuCandidateCanPauseModalController';
import InvigilatorMenuVideoSubtitleModalController from './modalCtrl/InvigilatorMenuVideoSubtitleModalController';
import InvigilatorMenuExtraTimeModalController from './modalCtrl/InvigilatorMenuExtraTimeModalController';
import InvigilatorMenuPauseModalController from './modalCtrl/InvigilatorMenuPauseModalController';
import QuitAssessmentModalController from './modalCtrl/QuitAssessmentModalController';
/**
 * InvigilatorMenuController - Controller for managing the invigilator menu interactions.
 * This class handles various functionalities such as authentication, modal management,
 * event logging, and user interactions within the invigilator menu.
 */
export class InvigilatorMenuController {
    /**
     * Constructor for InvigilatorMenuController.
     * Initializes dependencies and sets up initial state.
     *
     * @param {Object} $scope - AngularJS scope object.
     * @param {Object} $rootScope - AngularJS root scope object. 
     * @param {Object} AuthenticationBridgeService - Service for authentication.
     * @param {Object} LogBridgeService - Service for logging.
     * @param {Object} EventLogBridgeService - Service for event logging.
     * @param {Object} $signals - Service for signal handling.
     * @param {Object} $translate - Service for translation.
     * @param {Object} $timeout - AngularJS timeout service.
     * @param {Object} $interval - AngularJS interval service.
     * @param {Object} $routeParams - AngularJS route parameters.
     * @param {Object} $location - AngularJS location service.
     * @param {Object} UserInfoService - Service for user information.
     * @param {Object} $uibModal - AngularUI Bootstrap modal service.
     * @param {Object} IAAPreferencesService - Service for IAA preferences.
     * @param {Object} TaskCountdownService - Service for task countdown.
     * @param {Object} $window - AngularJS window service.
     * @param {Object} TaskInfoService - Service for task information.
     * @param {Object} $filter - AngularJS filter service.
     */
    constructor($scope, $rootScope, AuthenticationBridgeService, LogBridgeService, EventLogBridgeService, $signals, $translate, $timeout, $interval, $routeParams, $location, UserInfoService, $uibModal, IAAPreferencesService, TaskCountdownService, $window, TaskInfoService, $filter) {
        this.$scope = $scope;
        this.$rootScope = $rootScope;
        this.$signals = $signals;
        this.$translate = $translate;
        this.$timeout = $timeout;
        this.$interval = $interval;
        this.$routeParams = $routeParams;
        this.$location = $location;
        this.UserInfoService = UserInfoService;
        this.$uibModal = $uibModal;
        this.IAAPreferencesService = IAAPreferencesService;
        this.TaskCountdownService = TaskCountdownService;
        this.$window = $window;
        this.TaskInfoService = TaskInfoService;
        this.$filter = $filter;

        this.$logger = LogBridgeService;
        this.$auth = AuthenticationBridgeService;
        this.$eventLog = EventLogBridgeService;

        this.isSystemLangSelectable = TaskInfoService.getTaskInfo().isSystemLangSelectable();
        this.isUrdu = TaskInfoService.getTaskInfo().getISOInputLanguageCode() === 'ur_UR';

        this.isOpenModal = {
            extraTime: false,
            pause: false,
            candidateCanPause: false,
            videoSubtitle: false,
            quitAssessment: false,
            quitEarly: false,
            eventId: "wtf",
        };

        this.modalInstance = {
            quitAssessmentModalInstance: null,
            extraTimeModalInstance: null,
            pauseModalInstance: null,
            candidateCanPauseModalInstance: null,
            videoSubtitleModalInstance: null
        };

        this.$scope.safeApply = this.safeApply.bind(this);
        this.candidateUUID = this.$auth.getUserUUID();
        this.candidate = this.UserInfoService.getUserInfo(this.candidateUUID);
        this.taskCountdownService = this.TaskCountdownService.getInstance();

        this.$scope.log = [];
        this.$scope._logIdentifier = 'InvigilatorMenuController';
        this.$logger.logEnter(this.$scope, 'initialisation');

        this.$signals.remove("updateEventLog");
        this.$signals.on('updateEventLog', () => this.updateLog());

        this.$scope.iaaPreferences = this.IAAPreferencesService.getPreferences();
        this.$scope.iaaPreferences['zoomLevel'] = !isNaN(parseInt(this.$scope.iaaPreferences['zoomLevel'], 10)) ? parseInt(this.$scope.iaaPreferences['zoomLevel']) : 100;

        this.$scope.isRunning = this.taskCountdownService.isRunning;
        this.$scope.isPaused = this.taskCountdownService.isPaused();
        this.$scope.isOldPause = this.taskCountdownService.isPaused();

        this.$scope.returnTo = angular.isDefined(this.$routeParams.returnTo) ? atob(this.$routeParams.returnTo) : null;

        this.returnPath = angular.isDefined(this.$scope.returnTo) && this.$scope.returnTo ? this.$scope.returnTo : "/instructions/" + this.candidateUUID;

        if (this.$rootScope.iaaPreUnlocked) {
            this.$rootScope.iaaPreUnlocked = false;
            this.$scope.invigilatorIsUnlocked = true;
            this._pauseInInvigilatorMenu();
            this.updateLog();
        } else {
            this.$scope.invigilatorIsUnlocked = false;
        }
 

        if (this.$scope.isPaused && this.taskCountdownService.invigilatorRequired()) {
            this.$location.path(this.returnPath);
            return false;
        }

        this.$scope.onCancelBtn = this.onCancelBtn.bind(this);
        this.$scope.onQuitAssessment = this.onQuitAssessment.bind(this);
        this.$scope.onUnlockBtn = this.onUnlockBtn.bind(this);
        this.$scope.closeUnlockErrorMessage = this.closeUnlockErrorMessage.bind(this);
        this.$scope.onKeyupPassword = this.onKeyupPassword.bind(this);
        this.$scope.onOpenExtraTime = this.onOpenExtraTime.bind(this);
        this.$scope.onOpenPause = this.onOpenPause.bind(this);
        this.$scope.onCandidateCanPause = this.onCandidateCanPause.bind(this);
        this.$scope.onVideoSubtitle = this.onVideoSubtitle.bind(this);
        this.$scope.onChangeArr = this.onChangeArr.bind(this);
        this.$scope.increaseZoom = this.increaseZoom.bind(this);
        this.$scope.decreaseZoom = this.decreaseZoom.bind(this);

        this.$signals.remove("tryInvigilatorAuthenticationFailed");
        this.$signals.on("tryInvigilatorAuthenticationFailed", () => this._tryInvigilatorAuthenticationFailed());

        this.$signals.remove("tryInvigilatorAuthenticationSuccess");
        this.$signals.on("tryInvigilatorAuthenticationSuccess", (data) => this._tryInvigilatorAuthenticationSuccess(data));

        this.$scope.$on('$destroy', () => this.onDestroy());
    }

    /**
     * Safely applies a function to the AngularJS scope.
     * Prevents conflicts during $apply or $digest phases.
     *
     * @param {Function} fn - Function to apply to the scope.
     */
    safeApply(fn) {
        var phase = this.$scope.$root ? this.$scope.$root.$$phase : null;
        if (phase == '$apply' || phase == '$digest') {
            if (fn && (typeof(fn) === 'function')) {
                fn();
            }
        } else {
            this.$scope.$apply(fn);
        }
    }

    /**
     * Updates the event log by fetching events and updating the scope.
     */
    updateLog() {
        console.error('updateLog');
        this.$scope.safeApply(() => {
            const log = this.$eventLog.getEvents();
            const logArray = [];
            console.log('updateLog log', log);
            angular.forEach(log, (value, key) => {
                
                const _eventTypeObj = _.find(this.TaskInfoService.getTaskInfo().getEventTypes(), (event) => {
                    return event.id === value.eventReason || event.id === value.eventType;
                });
                if (_eventTypeObj && _eventTypeObj.visible_auditlog) {
                    value.tstamp = moment(value.ts).unix();
                    logArray.push(value);
                }
                console.log('updateLog value', value, _eventTypeObj.visible_auditlog, _eventTypeObj);
            });
            console.log('updateLog logArray', logArray);
            this.$scope.log = logArray;
        });
    }

    /**
     * Pauses the task countdown if it is running and not already paused.
     * Logs the pause event.
     */
    _pauseInInvigilatorMenu() {
        if (this.$scope.isRunning && !this.$scope.isPaused) {
            this.taskCountdownService.togglePause(this.taskCountdownService.invigilatorRequired());
            this.$eventLog.logEvent(this.$translate.instant("MODULES.ADMIN.EVENTLOG_PAUSE_INVIGILATOR_MENU"), "pauseForInvMenu", "", "", {});
            this.$scope.isRunning = this.taskCountdownService.isRunning;
            this.$scope.isPaused = this.taskCountdownService.isPaused();
        }
    }

    /**
     * Handles the cancel button click event.
     * Resumes the task countdown if paused and navigates to the return path.
     */
    onCancelBtn() {
        if (this.taskCountdownService.isPaused()) {
            this.taskCountdownService.togglePause(this.taskCountdownService.invigilatorRequired());
        }
        this.$location.path(this.returnPath);
    }

    /**
     * Handles the quit assessment event.
     * Opens a modal for quitting the assessment and logs the event.
     *
     * @param {boolean} earlyQuit - Flag indicating if the quit is early.
     */
    onQuitAssessment(earlyQuit) {
        if (!this.isOpenModal.quitAssessment) {
            this.isOpenModal.quitAssessment = true;
            this.isOpenModal.quitEarly = angular.isDefined(earlyQuit) ? Boolean(earlyQuit) : false;

            if (!earlyQuit) {
                this.isOpenModal.eventId = 'finishEarlyByInvigilator';
            } else {
                this.isOpenModal.eventId = 'leavePackageEarlyByInvigilator';
            }

            this.modalInstance.quitAssessmentModalInstance = this.$uibModal.open({
                templateUrl: "/views/ib/modules/invigilatorMenu/modal/quitAssessmentModal.html",
                windowClass: "quit-assessment-modal",
                transclude: false,
                backdrop: 'static',
                keyboard: false,
                controller: QuitAssessmentModalController,
                resolve: {
                    quitEarly: () => this.isOpenModal.quitEarly,
                    eventId: () => this.isOpenModal.eventId
                }
            });

            this.modalInstance.quitAssessmentModalInstance.result.then(() => {
                this.isOpenModal.quitAssessment = false;
            }, () => {
                this.isOpenModal.quitAssessment = false;
            });
        }
    }

    /**
     * Handles the invigilator authentication failure.
     * Logs the failure event and updates the scope.
     */
    _tryInvigilatorAuthenticationFailed() {
        this.$scope.safeApply(() => {
            console.log('_tryInvigilatorAuthenticationFailed');
            this.$scope.invigilatorUnlockCodeInputDisabled = false;
            this._unlockErrorMessage(this.$translate.instant('MODULES.ADMIN.EVENTLOG_INVIGILATOR_UNLOCK_FAILED'));

            var eMsg = this.$translate.instant('MODULES.ADMIN.EVENTLOG_INVIGILATOR_UNLOCK_FAILED');
            this.$eventLog.logEvent(eMsg, 'invLoginFailed', '', '', {});
            this.$scope.invigilatorUnlockCode = null;
        });
    }

    /**
     * Handles the invigilator authentication success.
     * Logs the success event and updates the scope.
     *
     * @param {string} data - JSON string containing authentication result.
     */
    _tryInvigilatorAuthenticationSuccess(data) {
        this.$scope.safeApply(() => {
            const result = typeof(data) === 'object' ? data : JSON.parse(data);

            if (!angular.isObject(result) || result.type !== 'invigilator') {
                this.$logger.warn("Received invalid user data from invigilator login" + JSON.stringify(result));
                this._tryInvigilatorAuthenticationFailed();
            } else if (result.type === 'invigilator') {
                console.log('_tryInvigilatorAuthenticationSuccess', result);
                this.$scope.invigilatorIsUnlocked = true;
                this.$signals.remove("tryInvigilatorAuthenticationFailed");
                this.$signals.remove("tryInvigilatorAuthenticationSuccess");

                var eMsg = this.$translate.instant('MODULES.ADMIN.EVENTLOG_INVIGILATOR_UNLOCK_SUCCESS');
                this.$eventLog.logEvent(eMsg, 'invLoginSuccess', '', '', {});
                this.$eventLog.logEvent(eMsg, 'invLoginSuccess', '', '', {});

                if (this.$scope.isOldPause && this.$scope.isRunning) {
                    this.$scope.isOldPause = false;
                    this.$eventLog.logEvent(this.$translate.instant("MODULES.ADMIN.EVENTLOG_APPLICATION_RESUME_BY_INVIGILATOR"), "resumeByInv", '', "", {});
                } else {
                    this._pauseInInvigilatorMenu();
                }

                this.updateLog();
                this.$scope.invigilatorUnlockCodeInputDisabled = false;
            } else {
                this._tryInvigilatorAuthenticationFailed();
            }
        });
    }

    /**
     * Handles the unlock button click event.
     * Attempts invigilator authentication and updates the scope.
     */
    onUnlockBtn() {
        this.$scope.errorMessage = null;
        if (this.$scope.invigilatorUnlockCode) {
            this.$scope.invigilatorUnlockCodeInputDisabled = true;
            this.$auth.tryInvigilatorAuthentication(this.$scope.invigilatorUnlockCode);
        } else {
            this._unlockErrorMessage(this.$translate.instant('MODULES.ADMIN.EVENTLOG_INVIGILATOR_UNLOCK_EMPTY'));
        }
    }

    /**
     * Closes the unlock error message if it is displayed.
     */
    closeUnlockErrorMessage() {
        if (this.$scope.unlockHasError) {
            this._unlockErrorMessage(null);
        }
    }

    /**
     * Handles the keyup event on the password input.
     * Closes the unlock error message and triggers unlock on Enter key.
     *
     * @param {Object} $event - AngularJS event object.
     */
    onKeyupPassword($event) {
        this.closeUnlockErrorMessage();
        if ($event.keyCode === 13) {
            this.onUnlockBtn();
        }
    }

    /**
     * Sets or clears the unlock error message.
     * Automatically clears the message after 5 seconds if set.
     *
     * @param {string|null} message - Error message to display.
     */
    _unlockErrorMessage(message) {
        if (!message) {
            this.$scope.unlockHasError = false;
            this.$scope.unlockErrorMessage = null;
            return false;
        }
        this.$scope.unlockHasError = true;
        this.$scope.unlockErrorMessage = message;

        if (this._removeUnlockHasErrorInterval) this.$interval.cancel(this._removeUnlockHasErrorInterval);
        this._removeUnlockHasErrorInterval = this.$interval(() => {
            this._unlockErrorMessage(null);
        }, 5000);
    }

    /**
     * Sets the position of a modal dialog relative to a target element.
     *
     * @param {Object} target - Target element to position the modal relative to.
     * @param {string} modalClass - CSS class of the modal to position.
     */
    _setModalPosition(target, modalClass) {
        var isSetPosttion = false;
        var findModalInterval;
        var setPosition = () => {
            if (angular.element('.' + modalClass).find('.modal-dialog').length > 0) {
                angular.element('.' + modalClass).find('.modal-dialog').css({
                    opacity: 0
                });
                angular.element('.modal-backdrop').css({
                    opacity: 0
                });
                this.$timeout(() => {
                    var top = angular.element(target).offset().top - angular.element(target).outerHeight() - this.$window.scrollY;
                    var modalHeight = angular.element('.' + modalClass).find('.modal-dialog').height();
                    if ((top + modalHeight + (this.$scope.iaaPreferences['zoomLevel'] / 100) * modalHeight) > this.$window.innerHeight) {
                        top = this.$window.innerHeight - modalHeight - 10;
                    }
                    angular.element('.' + modalClass).find('.modal-dialog').css({
                        top: top + 'px',
                        opacity: 1
                    });
                    angular.element('.modal-backdrop').css({
                        opacity: 0.5
                    });
                    isSetPosttion = true;
                    this.$interval.cancel(findModalInterval);
                }, 10);
            }
        };
        this.$timeout(() => {
            findModalInterval = this.$interval(() => {
                setPosition();
            }, 1);
            setPosition();
        });
    }

    /**
     * Opens a modal based on the provided IAA key.
     *
     * @param {string} _iaaKey - The key to determine which modal to open.
     * @returns {boolean} - Returns true if the modal should be opened, false otherwise.
     */
    _openModal(_iaaKey) {
        var _eventTypes = angular.copy(this.TaskInfoService.getTaskInfo().getEventTypes());
        var _filteredEventType = this.$filter('filter')(_eventTypes, { id: _iaaKey }, true)[0];
        var _existsReason = _filteredEventType.reasonPickList && _filteredEventType.reasonPickList.length > 0 ? true : false;

        if (_filteredEventType.explanatoryNote === 'off' && !_existsReason) {
            this.$scope.iaaPreferences[_iaaKey] = !this.$scope.iaaPreferences[_iaaKey];

            this.$timeout(() => {
                this.updateLog();
            });
            return false;
        }
        return true;
    }

    /**
     * Handles the opening of the extra time modal.
     *
     * @param {Object} $event - The event object.
     * @returns {boolean} - Returns false if the modal is already open.
     */
    onOpenExtraTime($event) {
        if (this.isOpenModal.extraTime) return false;
        this.isOpenModal.extraTime = true;
        this._setModalPosition($event.target, 'extra-time-modal');

        this.modalInstance.extraTimeModalInstance = this.$uibModal.open({
            templateUrl: "/views/ib/modules/invigilatorMenu/modal/extraTimeModal.html",
            windowClass: "extra-time-modal",
            backdrop: true,
            keyboard: false,
            controller: InvigilatorMenuExtraTimeModalController,
            resolve: {
                candidate: () => this.candidate,
                taskCountdownService: () => this.taskCountdownService,
                $eventLog: () => this.$eventLog
            }
        });

        this.modalInstance.extraTimeModalInstance.result.then(() => {
            this.isOpenModal.extraTime = false;
            if (this.modalInstance.extraTimeModalInstance.confirm) {
                this.$timeout(() => {
                    this.updateLog();
                });
            }
        }, () => {
            this.isOpenModal.extraTime = false;
        });
    }

    /**
     * Handles the opening of the pause modal.
     *
     * @param {Object} $event - The event object.
     * @returns {boolean} - Returns false if the modal is already open.
     */
    onOpenPause($event) {
        if (this.isOpenModal.pause) return false;
        this.isOpenModal.pause = true;
        this._setModalPosition($event.target, 'pause-modal');

        this.modalInstance.pauseModalInstance = this.$uibModal.open({
            templateUrl: "/views/ib/modules/invigilatorMenu/modal/pauseModal.html",
            windowClass: "pause-modal",
            backdrop: true,
            keyboard: false,
            controller: InvigilatorMenuPauseModalController,
            resolve: {
                taskCountdownService: () => this.TaskCountdownService,
                $eventLog: () => this.$eventLog
            }
        });

        this.modalInstance.pauseModalInstance.result.then(() => {
            this.isOpenModal.pause = false;
            if (this.modalInstance.pauseModalInstance.confirm) {
                this.$scope.isPaused = this.TaskCountdownService.isPaused();
                this.$location.path(this.returnPath);
            }
        }, () => {
            this.isOpenModal.pause = false;
        });
    }

    /**
     * Handles the opening of the candidate can pause modal.
     *
     * @param {Object} $event - The event object.
     * @returns {boolean} - Returns false if the modal is already open.
     */
    onCandidateCanPause($event) {
        if (this.isOpenModal.candidateCanPause) return false;
        this.isOpenModal.candidateCanPause = true;
        this._setModalPosition($event.target, 'candidate-pause-modal');

        this.modalInstance.candidateCanPauseModalInstance = this.$uibModal.open({
            templateUrl: "/views/ib/modules/invigilatorMenu/modal/candidateCanPauseModal.html",
            windowClass: "candidate-pause-modal",
            backdrop: true,
            keyboard: false,
            controller: InvigilatorMenuCandidateCanPauseModalController,
            resolve: {
                taskCountdownService: () => this.TaskCountdownService,
                $eventLog: () => this.$eventLog,
                iaaPreferences: () => this.$scope.iaaPreferences,
                IAAPreferencesService: () => this.IAAPreferencesService
            }
        });

        this.modalInstance.candidateCanPauseModalInstance.result.then(() => {
            this.isOpenModal.candidateCanPause = false;
            if (this.modalInstance.candidateCanPauseModalInstance.confirm) {
                this.$timeout(() => {
                    this.updateLog();
                });
            }
        }, () => {
            this.isOpenModal.candidateCanPause = false;
        });
    }

    /**
     * Handles the opening of the video subtitle modal.
     *
     * @param {Object} $event - The event object.
     * @returns {boolean} - Returns false if the modal is already open or should not be opened.
     */
    onVideoSubtitle($event) {
        if (!this._openModal('videoSubtitleAutoEnable') || this.isOpenModal.videoSubtitle) return false;
        this.isOpenModal.videoSubtitle = true;
        this._setModalPosition($event.target, 'video-subtitle-modal');

        this.modalInstance.videoSubtitleModalInstance = this.$uibModal.open({
            templateUrl: "/views/ib/modules/invigilatorMenu/modal/videoSubtitleModal.html",
            windowClass: "video-subtitle-modal",
            backdrop: true,
            keyboard: false,
            controller: InvigilatorMenuVideoSubtitleModalController,
            resolve: {
                taskCountdownService: () => this.TaskCountdownService,
                $eventLog: () => this.$eventLog,
                iaaPreferences: () => this.$scope.iaaPreferences,
                IAAPreferencesService: () => this.IAAPreferencesService
            }
        });

        this.modalInstance.videoSubtitleModalInstance.result.then(() => {
            this.isOpenModal.videoSubtitle = false;
            if (this.modalInstance.videoSubtitleModalInstance.confirm) {
                this.$timeout(() => {
                    this.updateLog();
                });
            }
        }, () => {
            this.isOpenModal.videoSubtitle = false;
        });
    }

    /**
     * Handles changes in array values.
     *
     * @param {string} val - The value that changed.
     * @param {Object} $event - The event object.
     * @returns {boolean} - Returns false if the change should not proceed.
     */
    onChangeArr(val, $event) {
        if (val === 'candidateCanPause') {
            const _eventTypes = angular.copy(this.TaskInfoService.getTaskInfo().getEventTypes());
            const _filteredEventType = this.$filter('filter')(_eventTypes, { id: 'candidateCanPauseEnable' }, true)[0];
            if (_filteredEventType) {
                this.onCandidateCanPause($event);
                return false;
            }
        } else if (val === 'videoSubtitleAutoEnable') {
            const _eventTypes = angular.copy(this.TaskInfoService.getTaskInfo().getEventTypes());
            const _filteredEventType = this.$filter('filter')(_eventTypes, { id: 'videoSubtitleAutoEnable' }, true)[0];

            if (_filteredEventType) {
                this.onVideoSubtitle($event);
                return false;
            }
        }
        this.$scope.iaaPreferences[val] = !this.$scope.iaaPreferences[val];
        this.updateLog();
    }

    /**
     * Increases the zoom level.
     *
     * @returns {boolean} - Returns false if the zoom level cannot be increased.
     */
    increaseZoom() {
        if (this.$scope.iaaPreferences['zoomLevel'] + 5 > 150) {
            return false;
        }
        this.$scope.iaaPreferences['zoomLevel'] = this.$scope.iaaPreferences['zoomLevel'] + 5;
    }

    /**
     * Decreases the zoom level.
     *
     * @returns {boolean} - Returns false if the zoom level cannot be decreased.
     */
    decreaseZoom() {
        if (this.$scope.iaaPreferences['zoomLevel'] - 5 < 100) {
            return false;
        }
        this.$scope.iaaPreferences['zoomLevel'] = this.$scope.iaaPreferences['zoomLevel'] - 5;
    }

    /**
     * Cleanup method called when the controller is destroyed.
     * Removes signal listeners and closes all modals.
     */
    onDestroy() {
        this.$signals.remove("tryInvigilatorAuthenticationFailed");
        this.$signals.remove("tryInvigilatorAuthenticationSuccess");

        angular.forEach(this.modalInstance, (modalInstanceValue, modalInstanceKey) => {
            if (modalInstanceValue !== null) {
                modalInstanceValue.close();
            }
        });
    }
}

export default function registerInvigilatorMenuController(assessmentFrontendControllers) {
    assessmentFrontendControllers.controller('InvigilatorMenuController', ['$scope', '$rootScope', 'AuthenticationBridgeService', 'LogBridgeService', 'EventLogBridgeService', '$signals', '$translate', '$timeout', '$interval', '$routeParams', '$location', 'UserInfoService', '$uibModal', 'IAAPreferencesService', 'TaskCountdownService', '$window', 'TaskInfoService', '$filter', InvigilatorMenuController]);
};